/**
 * SA Tools API Client
 * Axios instance configured for SA Tools API Gateway
 */
import axios from 'axios';

// Token provider function - set by the app to inject auth tokens
let tokenProvider = null;

/**
 * Set the token provider function for API authorization.
 * Call this with getApiToken from AuthContext to enable auth headers.
 * @param {Function} provider - async function that returns an access token
 */
export const setTokenProvider = (provider) => {
  tokenProvider = provider;
};

/**
 * Get API base URL based on environment
 */
const getApiBaseUrl = () => {
  // Development: use localhost or environment variable
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_SATOOLS_API_BASE_URL || 'http://localhost:3001';
  }

  // Production: use API Gateway URL from environment or default
  return process.env.REACT_APP_SATOOLS_API_BASE_URL ||
         'https://api.example.com/dev'; // Replace with actual API Gateway URL after deployment
};

// Create axios instance
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for long-running AI operations
});

// Request interceptor - add auth token and log requests
apiClient.interceptors.request.use(
  async (config) => {
    // Inject auth token if provider is set
    if (tokenProvider) {
      try {
        const token = await tokenProvider();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('[SA Tools API] Failed to acquire auth token:', error);
      }
    }
    console.log(`[SA Tools API] ${config.method.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[SA Tools API] Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    console.log(`[SA Tools API] Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error('[SA Tools API] Response Error:', error.response?.data || error.message);

    // Provide user-friendly error messages
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.error || error.response.data?.message || 'Server error occurred';
      throw new Error(`API Error (${error.response.status}): ${message}`);
    } else if (error.request) {
      // Request made but no response
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Request setup error
      throw new Error(`Request Error: ${error.message}`);
    }
  }
);

export default apiClient;
